import React, { Component } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import { Layout } from './components/Layout';
import { EmbedLayout } from './components/EmbedLayout';
import { isEmbedMode } from './utils/embed';
import { getToken, isTokenExpired } from './utils/auth';
import { recordSessionEvent } from './utils/sessionLog';
import { LocalizationProvider } from './contexts/LocalizationContext';
import { MenuProvider } from './contexts/MenuContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { PermissionProvider } from './contexts/PermissionContext';
import './custom.css';

/**
 * Auth kapısının login'e attığı her durumu teşhis günlüğüne yazar.
 *
 * Bu kapı sessizdi: token'ı geçersiz olan kullanıcı hiçbir mesaj görmeden login'e
 * düşüyor, token da localStorage'da kalıyordu — "sebepsiz atıldım" şikâyetlerinin
 * bir kısmı buradan geliyor olabilir. Token VARDI ama süresi dolmuşsa gerçekten bir
 * oturum kaybedilmiştir; sebebi Login'e taşı ki kullanıcı ne olduğunu görsün.
 * (Token hiç yoksa bu normal "giriş yapmadan korumalı adrese gitme" halidir.)
 */
function logAuthGateRedirect(pathname) {
  const hasToken = !!getToken();
  recordSessionEvent('auth-gate', { hasToken, tokenExpired: hasToken && isTokenExpired(), pathname });
  if (hasToken) {
    try { sessionStorage.setItem('sessionEndReason', 'expired'); } catch { /* yoksay */ }
  }
}

// A simple wrapper to conditionally apply the Layout
function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signin-oidc'
    || location.pathname === '/forgot-password' || location.pathname === '/reset-password'
    || location.pathname === '/change-password';

  const routeElements = (
    <Routes>
      {AppRoutes.map((route, index) => {
        const { element, ...rest } = route;
        return <Route key={index} {...rest} element={element} />;
      })}
    </Routes>
  );

  if (isLoginPage) {
    return routeElements;
  }

  // Auth gate: korumalı bir route'a geçerli token olmadan (hiç giriş yapılmamış ya
  // da süresi dolmuş) erişilirse login'e yönlendir. Aksi halde sayfa iskeleti render
  // olur, veri 401 alır ve kullanıcı boş ekranda kalırdı ("login olmadan /products").
  // Public sayfalar (login/forgot/reset/oidc) yukarıda isLoginPage ile zaten muaf.
  const isAuthenticated = !!getToken() && !isTokenExpired();
  if (!isEmbedMode() && !isAuthenticated) {
    logAuthGateRedirect(location.pathname);
    return <Navigate to="/" replace />;
  }

  // İlk giriş zorunlu şifre değişikliği kapısı: kullanıcı sistemin ürettiği geçici şifreyle giriş
  // yaptıysa (login yanıtı mustChangePassword=true → localStorage 'user'a yazıldı), şifresini
  // belirleyene kadar hiçbir korumalı sayfaya erişemez; /change-password'a yönlendirilir.
  // (Değişim başarılı olunca bayrak temizlenir ve bu kapı açılır.)
  let mustChangePassword = false;
  try { mustChangePassword = JSON.parse(localStorage.getItem('user') || '{}')?.mustChangePassword === true; } catch { /* yoksay */ }
  if (!isEmbedMode() && isAuthenticated && mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // G5-T34.3/34.5: ?embed=true ise NavMenu olmadan EmbedLayout kullan
  if (isEmbedMode()) {
    return <EmbedLayout>{routeElements}</EmbedLayout>;
  }

  return (
    <Layout>
      {routeElements}
    </Layout>
  );
}

export default class App extends Component {
  static displayName = App.name;

  render() {
    return (
      <LocalizationProvider>
        <MenuProvider>
          <FavoritesProvider>
            <PermissionProvider>
              <AppContent />
            </PermissionProvider>
          </FavoritesProvider>
        </MenuProvider>
      </LocalizationProvider>
    );
  }
}
